import { useMemo, useState } from "react";
import { Button, Text, mergeClasses } from "@fluentui/react-components";
import {
  useUiStyles,
  IconTile,
  SparkleIcon,
  TeamsIcon,
  CalendarIcon,
  DocumentIcon,
  ShieldIcon,
  RocketIcon,
  ArrowRightIcon,
} from "./ui";
import { useLanguage } from "../i18n/LanguageContext";

const STORAGE_PREFIX = "ma-lite-onboarding-seen:";

export function hasSeenOnboarding(userId: string): boolean {
  try {
    return localStorage.getItem(STORAGE_PREFIX + userId) === "1";
  } catch {
    return true; // if storage is unavailable, don't block the app on a tour that can't be dismissed
  }
}

function markOnboardingSeen(userId: string): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + userId, "1");
  } catch {
    // ignore
  }
}

interface Step {
  icon: JSX.Element;
  title: string;
  body: string;
}

const STEP_ICONS = [<SparkleIcon key="0" />, <TeamsIcon key="1" />, <CalendarIcon key="2" />, <DocumentIcon key="3" />, <ShieldIcon key="4" />, <RocketIcon key="5" />, <ShieldIcon key="6" />];

function buildSteps(isAdmin: boolean, translatedSteps: { title: string; body: string }[]): Step[] {
  const steps: Step[] = translatedSteps
    .slice(0, 6)
    .map((step, i) => ({ icon: STEP_ICONS[i], title: step.title, body: step.body }));

  if (isAdmin && translatedSteps[6]) {
    steps.push({ icon: STEP_ICONS[6], title: translatedSteps[6].title, body: translatedSteps[6].body });
  }

  return steps;
}

interface OnboardingTourProps {
  userId: string;
  isAdmin: boolean;
  onFinish: () => void;
}

/**
 * A deliberately detailed, decisive first-run walkthrough — shown once (per
 * user, persisted in localStorage) right after sign-in, and re-openable any
 * time from the "How it works" link on Home. Goal: the user should never be
 * unsure what to do next.
 */
export function OnboardingTour({ userId, isAdmin, onFinish }: OnboardingTourProps) {
  const s = useUiStyles();
  const { t, tRaw } = useLanguage();
  const translatedSteps = tRaw<{ title: string; body: string }[]>("onboarding.steps");
  const steps = useMemo(() => buildSteps(isAdmin, translatedSteps), [isAdmin, translatedSteps]);
  const [index, setIndex] = useState(0);
  const isLast = index === steps.length - 1;
  const step = steps[index];

  const finish = () => {
    markOnboardingSeen(userId);
    onFinish();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "var(--colorNeutralBackground1)",
        zIndex: 1000,
        overflowY: "auto",
      }}
    >
      <div className={s.landingWrap}>
        <div className={mergeClasses(s.card, s.accentTop)} style={{ padding: "26px 22px" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginBottom: "18px" }}>
            {steps.map((_, i) => (
              <span
                key={i}
                style={{
                  width: i === index ? "18px" : "6px",
                  height: "6px",
                  borderRadius: "3px",
                  backgroundColor:
                    i === index ? "var(--colorBrandBackground)" : "var(--colorNeutralStroke2)",
                  transition: "width 150ms",
                }}
              />
            ))}
          </div>

          <div style={{ textAlign: "center", marginBottom: "8px" }}>
            <IconTile variant="brand">{step.icon}</IconTile>
          </div>
          <Text size={500} weight="semibold" block style={{ textAlign: "center", marginTop: "10px" }}>
            {step.title}
          </Text>
          <Text
            size={300}
            block
            style={{
              textAlign: "center",
              marginTop: "10px",
              color: "var(--colorNeutralForeground2)",
              lineHeight: 1.6,
            }}
          >
            {step.body}
          </Text>

          <div style={{ display: "flex", gap: "10px", marginTop: "26px" }}>
            {index > 0 && (
              <Button appearance="secondary" onClick={() => setIndex((i) => i - 1)}>
                {t("onboarding.back")}
              </Button>
            )}
            <Button
              appearance="primary"
              icon={isLast ? undefined : <ArrowRightIcon />}
              iconPosition="after"
              style={{ flex: 1 }}
              onClick={() => (isLast ? finish() : setIndex((i) => i + 1))}
            >
              {isLast ? t("onboarding.finish") : t("onboarding.next")}
            </Button>
          </div>
          {!isLast && (
            <Button appearance="transparent" size="small" style={{ marginTop: "6px", width: "100%" }} onClick={finish}>
              {t("onboarding.skip")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
